import React, { useState, useEffect } from 'react'

import { FiPlus, FiEdit, FiCopy, FiShare2, FiLock, FiTrash2, FiHome, FiDownload, FiUpload } from 'react-icons/fi'

import { Modal } from '@/components/ui'
import { useDashboard } from '@/hooks'

import { type PanelView } from '../config/DashboardConfigPanel'

export interface LandingProps {
  onSelectedView: (view: PanelView) => void
  onClose: () => void
}

const landingOptions: Array<{ title: string; description: string; icon: React.ReactNode; view: PanelView }> = [
  {
    title: 'Add Insight',
    description: 'Select from various insights to monitor assets, alarms, alerts, etc',
    icon: <FiPlus />,
    view: 'AddInsight',
  },
  {
    title: 'Rename or Change Icon',
    description: 'Rename this dashboard or choose a different icon',
    icon: <FiEdit />,
    view: 'RenameDashboard',
  },
  {
    title: 'Make Default',
    description: 'Make a this dashboard the default',
    icon: <FiHome />,
    view: 'DefaultDashboard',
  },
  {
    title: 'Copy Dashboard',
    description: 'Make a copy of this dashboard',
    icon: <FiCopy />,
    view: 'CopyDashboard',
  },
  {
    title: 'Export Dashboard',
    description: 'Export this dashboard to a file that can be shared',
    icon: <FiDownload />,
    view: 'ExportDashboard',
  },
  {
    title: 'Import Dashboard',
    description: 'Import a dashboard from an export file',
    icon: <FiUpload />,
    view: 'ImportDashboard',
  },
  {
    title: 'Share Dashboard',
    description: 'Share this dashboard',
    icon: <FiShare2 />,
    view: 'ShareDashboard',
  },
  {
    title: 'Dashboard-Wide Parameters',
    description: 'Set parameters that apply to all Insights on this Dashboard',
    icon: <FiLock />,
    view: 'Permissions',
  },
]

const Landing: React.FC<LandingProps> = ({ onSelectedView, onClose }) => {
  const dashboardContext = useDashboard()
  const [modalOpen, setModalOpen] = useState(false)
  const modalRef = React.useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (modalRef.current) {
      const dialog = modalRef.current.closest('dialog') as HTMLDialogElement
      if (dialog) {
        if (modalOpen) {
          if (!dialog.open) {
            dialog.showModal()
          }
        } else if (dialog.open) {
          dialog.close()
        }
      }
    }
  }, [modalOpen])

  const handleClose = () => {
    dashboardContext.removeDashboard({ slug: dashboardContext.currentDashboard.slug })
    setModalOpen(false)
    onClose()
  }
  const currentDashboard = dashboardContext.currentDashboard
  const defaultDashboard = dashboardContext.configuration.dashboards.find((d) => d.isDefault)
  const landingButtons = landingOptions.filter(
    (o) => o.view !== 'DefaultDashboard' || currentDashboard.slug !== defaultDashboard?.slug
  )
  return (
    <div className="flex flex-col items-center gap-4 p-4 justify-between h-full">
      <div className="flex flex-col gap-4 w-full">
        {landingButtons.map((option, index) => (
          <button
            key={index}
            onClick={() => {
              onSelectedView(option.view)
            }}
            className="py-4 px-6 hover:cursor-pointer bg-base-200 hover:bg-base-content/20 flex flex-col gap-2 rounded-md transition-all duration-150 w-full text-base-content border border-base-300 "
          >
            <div className="flex items-center justify-start gap-4 text-sm font-semibold ">
              {option.icon}
              {option.title}
            </div>
            <p className="text-left text-xs font-extralight">{option.description}</p>
          </button>
        ))}
      </div>
      {/* Highway to the danger zone */}
      <div className="flex flex-row w-full justify-center font-semibold text-sm">
        <button
          className="text-red-700 border-red-700 border px-4 py-2 rounded hover:cursor-pointer  hover:bg-red-700 hover:text-primary-content flex justify-center items-center gap-2"
          onClick={() => {
            setModalOpen(true)
          }}
        >
          <FiTrash2 />
          Remove Dashboard
        </button>
      </div>

      <Modal
        variation="danger"
        // id="delete-dashboard-modal"
        title={`Delete Dashboard: ${dashboardContext.currentDashboard.title}`}
        onHide={() => setModalOpen(false)}
        show={modalOpen}
        bodyContent={
          <div className="flex flex-col gap-4 mt-4" ref={modalRef}>
            <p className="mb-3">
              This will permanently delete this dashboard.
              <br />
              This action is irreversible.
            </p>
            <p>Please confirm this action</p>
          </div>
        }
        footerButtons={[
          {
            label: 'Delete Dashboard',
            onClick: () => {
              handleClose()
              dashboardContext.removeDashboard({ slug: dashboardContext.currentDashboard.slug })
              setModalOpen(false)
            },
            class: 'btn-error',
          },
          {
            label: 'Cancel',
            onClick: () => {
              setModalOpen(false)
            },
          },
        ]}
      />
    </div>
  )
}

export default Landing
